import { User, Location } from '../types';
import {
  HomeIcon,
  ShoppingBagIcon,
  ClockIcon,
  CogIcon,
  BuildingStorefrontIcon,
  DocumentTextIcon,
  BanknotesIcon,
  ArchiveBoxIcon,
  BookOpenIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  TruckIcon,
  ArrowsRightLeftIcon,
  ChartBarIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';

export type UserRole = 'viewer' | 'warehouse_staff' | 'company_admin' | 'arkx_admin';

export type Permission = 
  | 'read_products' 
  | 'create_products' 
  | 'update_products' 
  | 'delete_products'
  | 'read_inventory' 
  | 'adjust_inventory' 
  | 'read_transfers' 
  | 'create_transfers' 
  | 'read_transactions' 
  | 'manage_users' 
  | 'manage_company' 
  | 'read_analytics'
  | 'manage_locations'
  | 'modify_main_warehouse_inventory'
  // Admin-specific permissions
  | 'admin_view_all_companies'
  | 'admin_manage_users'
  | 'admin_manage_warehouses'
  | 'admin_manage_size_scales'
  | 'admin_manage_colors'
  | 'admin_manage_products'
  | 'admin_view_audit_logs'
  | 'admin_adjust_inventory';

// Role hierarchy and permissions mapping
const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  viewer: [
    'read_products',
    'read_inventory',
    'read_transfers',
    'read_transactions',
  ],
  warehouse_staff: [
    'read_products',
    'create_products',
    'update_products',
    'read_inventory',
    'adjust_inventory',
    'read_transfers',
    'create_transfers',
    'read_transactions',
  ],
  company_admin: [
    'read_products',
    'create_products',
    'update_products',
    'delete_products',
    'read_inventory',
    'adjust_inventory',
    'read_transfers',
    'create_transfers',
    'read_transactions',
    'manage_users',
    'manage_company',
    'read_analytics',
    'manage_locations',
  ],
  arkx_admin: [
    'read_products',
    'create_products',
    'update_products',
    'delete_products',
    'read_inventory',
    'adjust_inventory',
    'read_transfers',
    'create_transfers',
    'read_transactions',
    'manage_users',
    'manage_company',
    'read_analytics',
    'manage_locations',
    'modify_main_warehouse_inventory', // Only ARKX admins can modify main warehouse inventory
    // Admin-specific permissions
    'admin_view_all_companies',
    'admin_manage_users',
    'admin_manage_warehouses',
    'admin_manage_size_scales',
    'admin_manage_colors',
    'admin_manage_products',
    'admin_view_audit_logs',
    'admin_adjust_inventory',
  ],
};

export class PermissionManager {
  /**
   * Check if a user has a specific permission
   */
  static hasPermission(user: User | null, permission: Permission): boolean {
    if (!user || !user.role) return false;
    
    const rolePermissions = ROLE_PERMISSIONS[user.role];
    return rolePermissions?.includes(permission) || false;
  }

  /**
   * Check if a user can modify inventory at a specific location
   */
  static canModifyInventoryAtLocation(user: User | null, location: Location | null): boolean {
    if (!user || !location) return false;

    // ARKX admins can modify inventory anywhere
    if ((user.role as UserRole) === 'arkx_admin') {
      return true;
    }

    // Main warehouse (warehouse_type 'main') can only be modified by ARKX admins
    if (location.warehouse_type === 'main') {
      return this.hasPermission(user, 'modify_main_warehouse_inventory');
    }

    // Client warehouses can be modified by company admins and warehouse staff
    // but only if they belong to the same company
    if (location.warehouse_type === 'client' && location.company_id === user.company_id) {
      return this.hasPermission(user, 'adjust_inventory');
    }

    return false;
  }

  /**
   * Check if a user can create products at a specific location
   */
  static canCreateProductsAtLocation(user: User | null, location: Location | null): boolean {
    if (!user || !location) return false;

    // ARKX admins can create products anywhere
    if ((user.role as UserRole) === 'arkx_admin') {
      return true;
    }

    // For main warehouse, only ARKX admins can create products
    if (location.warehouse_type === 'main') {
      return (user.role as UserRole) === 'arkx_admin';
    }

    // For client warehouses, company users can create products in their own locations
    if (location.warehouse_type === 'client' && location.company_id === user.company_id) {
      return this.hasPermission(user, 'create_products');
    }

    return false;
  }

  /**
   * Check if a user can view a specific location
   */
  static canViewLocation(user: User | null, location: Location | null): boolean {
    if (!user || !location) {
      return false;
    }

    // ARKX admins can view all locations
    if ((user.role as UserRole) === 'arkx_admin') {
      return true;
    }

    // Main warehouse is visible to all authenticated users (read-only for non-ARKX)
    if (location.warehouse_type === 'main') {
      return true;
    }

    // Client warehouses are only visible to users from the same company
    if (location.warehouse_type === 'client') {
      return location.company_id === user.company_id;
    }

    return false;
  }

  /**
   * Check if a user can manage (create/edit/delete) a specific location
   */
  static canManageLocation(user: User | null, location: Location | null): boolean {
    if (!user) return false;

    // ARKX admins can manage all locations
    if ((user.role as UserRole) === 'arkx_admin') {
      return true;
    }

    // Main warehouse can only be managed by ARKX admins
    if (location?.warehouse_type === 'main') {
      return false;
    }

    // Company admins can manage their own client warehouses
    if (location?.warehouse_type === 'client' && location.company_id === user.company_id) {
      return this.hasPermission(user, 'manage_locations');
    }

    // For creating new locations (location is null), company admins can create client warehouses
    if (!location) {
      return this.hasPermission(user, 'manage_locations');
    }

    return false;
  }

  /**
   * Get locations that a user can view
   */
  static getViewableLocations(user: User | null, locations: Location[]): Location[] {
    if (!user) return [];

    return locations.filter(location => this.canViewLocation(user, location));
  }

  /**
   * Get locations where a user can modify inventory
   */
  static getInventoryModifiableLocations(user: User | null, locations: Location[]): Location[] {
    if (!user) return [];

    return locations.filter(location => this.canModifyInventoryAtLocation(user, location));
  }

  /**
   * Check if a user has any role-based permissions
   */
  static hasAnyPermission(user: User | null, permissions: Permission[]): boolean {
    if (!user) return false;

    return permissions.some(permission => this.hasPermission(user, permission));
  }

  /**
   * Get a user's role display name
   */
  static getRoleDisplayName(role: string): string {
    const roleNames: Record<string, string> = {
      viewer: 'Viewer',
      warehouse_staff: 'Warehouse Staff',
      company_admin: 'Company Admin',
      arkx_admin: 'ARKX Admin',
    };

    return roleNames[role] || 'Unknown';
  }

  /**
   * Check if user can see main ARKX warehouse
   */
  static canViewMainWarehouse(user: User | null): boolean {
    // All authenticated users can view the main warehouse (read-only for non-ARKX users)
    return !!user;
  }

  /**
   * Check if user can create their own warehouses
   */
  static canCreateOwnWarehouse(user: User | null): boolean {
    if (!user) return false;
    
    // Company admins can create their own warehouses
    return this.hasPermission(user, 'manage_locations');
  }

  /**
   * Get warning message when user tries to modify main warehouse inventory
   */
  static getMainWarehouseRestrictionMessage(user: User | null): string {
    if (!user) return 'You must be logged in to perform this action.';
    
    if (user.role === 'arkx_admin') return '';
    
    return 'Only ARKX administrators can modify inventory in the main warehouse. You can view items but cannot make changes.';
  }

  // ===== ADMIN-SPECIFIC HELPER METHODS =====

  /**
   * Check if user is arkx_admin
   */
  static isArkxAdmin(user: User | null): boolean {
    return user?.role === 'arkx_admin';
  }

  /**
   * Check if user can access admin panel
   */
  static canAccessAdminPanel(user: User | null): boolean {
    return this.isArkxAdmin(user);
  }

  /**
   * Get data scope for user (company vs global)
   */
  static getDataScope(user: User | null): 'company' | 'global' {
    return this.isArkxAdmin(user) ? 'global' : 'company';
  }

  /**
   * Get available filters based on user role and page
   */
  static getAvailableFilters(user: User | null, page: string): string[] {
    const baseFilters = ['search', 'status', 'date_range'];
    
    if (this.isArkxAdmin(user)) {
      // Admin gets additional cross-company filters
      return [...baseFilters, 'company_id', 'warehouse_id', 'user_id'];
    }
    
    return baseFilters;
  }

  /**
   * Check if user can manage preset items (colors, size scales)
   */
  static canManagePresetItems(user: User | null): boolean {
    return this.isArkxAdmin(user);
  }

  /**
   * Check if user can view audit logs
   */
  static canViewAuditLogs(user: User | null): boolean {
    return this.hasPermission(user, 'admin_view_audit_logs');
  }

  /**
   * Check if user can manage cross-company users
   */
  static canManageCrossCompanyUsers(user: User | null): boolean {
    return this.hasPermission(user, 'admin_manage_users');
  }

  /**
   * Check if user can adjust inventory across companies
   */
  static canAdjustCrossCompanyInventory(user: User | null): boolean {
    return this.hasPermission(user, 'admin_adjust_inventory');
  }

  /**
   * Get navigation items based on user role
   */
  static getNavigationItems(user: User | null) {
    const baseItems = [
      { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
      {
        name: 'Products',
        icon: ShoppingBagIcon,
        children: [
          { name: 'All Products', href: '/products', icon: ShoppingBagIcon },
          { name: 'Inventory', href: '/products/inventory', icon: ArchiveBoxIcon },
        ]
      },
      { name: 'Transfers', href: '/transfers', icon: ArrowsRightLeftIcon },
      {
        name: 'Darkstore',
        icon: BuildingStorefrontIcon,
        children: [
          { name: 'Brand', href: '/darkstore', icon: BuildingStorefrontIcon },
          { name: 'Listings', href: '/darkstore/listings', icon: RectangleStackIcon },
          { name: 'Orders', href: '/darkstore/orders', icon: DocumentTextIcon },
          { name: 'Payouts', href: '/darkstore/payouts', icon: BanknotesIcon },
        ]
      },
      { name: 'History', href: '/history', icon: ClockIcon },
      { name: 'Settings', href: '/settings', icon: CogIcon },
    ];

    if (this.isArkxAdmin(user)) {
      // Admin navigation - matches user requirements
      const adminItems = [
        { name: 'Dashboard', href: '/admin', icon: HomeIcon },
        {
          name: 'Products',
          icon: ShoppingBagIcon,
          children: [
            { name: 'All Products', href: '/admin/products', icon: ShoppingBagIcon },
            { name: 'Inventory', href: '/admin/products/inventory', icon: ArchiveBoxIcon },
          ]
        },
        { name: 'Transfers', href: '/admin/transfers', icon: ArrowsRightLeftIcon },
        {
          name: 'Darkstore',
          icon: BuildingStorefrontIcon,
          children: [
            { name: 'Brands', href: '/admin/darkstore', icon: BuildingStorefrontIcon },
            { name: 'Listings', href: '/admin/darkstore/listings', icon: RectangleStackIcon },
            { name: 'Orders', href: '/admin/orders', icon: DocumentTextIcon },
            { name: 'Deliveries', href: '/admin/deliveries', icon: TruckIcon },
            { name: 'Payouts', href: '/admin/payouts', icon: BanknotesIcon },
          ]
        },
        {
          name: 'Ledgers',
          icon: BookOpenIcon,
          children: [
            { name: 'Financial', href: '/admin/financial-ledger', icon: CurrencyDollarIcon },
            { name: 'Inventory', href: '/admin/inventory-ledger', icon: ClipboardDocumentListIcon },
          ]
        },
        {
          name: 'Settings',
          icon: CogIcon,
          children: [
            { name: 'General', href: '/admin/settings', icon: CogIcon },
            { name: 'Users', href: '/admin/users', icon: UsersIcon },
            { name: 'Companies', href: '/admin/companies', icon: BuildingOfficeIcon },
          ]
        },
        { name: 'Analytics', href: '/admin/analytics', icon: ChartBarIcon },
        { name: 'Audit Logs', href: '/admin/audit', icon: ShieldCheckIcon },
      ];
      return adminItems;
    }

    return baseItems;
  }

  /**
   * Get company display name for admin context
   */
  static getCompanyDisplayName(user: User | null): string {
    if (this.isArkxAdmin(user)) {
      return 'ARKX Admin';
    }
    return user?.company_name || user?.company?.name || 'Loading...';
  }

  /**
   * Check if item is editable by user (considering preset restrictions)
   */
  static canEditItem(user: User | null, item: { is_preset?: boolean; company_id?: number }): boolean {
    if (!user) return false;

    // Preset items can only be edited by arkx_admin
    if (item.is_preset) {
      return this.isArkxAdmin(user);
    }

    // Non-preset items can be edited by company users if they own it, or by admin
    if (this.isArkxAdmin(user)) {
      return true;
    }

    return item.company_id === user.company_id;
  }

  /**
   * Check if item can be deleted by user
   */
  static canDeleteItem(user: User | null, item: { is_preset?: boolean; company_id?: number }): boolean {
    if (!user) return false;

    // Preset items cannot be deleted by anyone
    if (item.is_preset) {
      return false;
    }

    // Non-preset items can be deleted by company users if they own it, or by admin
    if (this.isArkxAdmin(user)) {
      return true;
    }

    return item.company_id === user.company_id;
  }
}

export default PermissionManager;
